import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { QrCode } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ROUTES } from "../../routes";
import ModalShell from "../modal/ModalShell";
import ShareLinkBlock from "./ShareLinkBlock";
import { selectBookingPageSettings } from "../../features/trial-requests/trial-requests-slice";
import { fetchBookingPageSettings } from "../../features/trial-requests/slice-extensions/trial-requests-slice-extension";
import { bookingPageUrl } from "../../features/trial-requests/utils/bookingPage";

const TITLE_ID = "share-links-title";

/**
 * The members' portal and the live booking page, ready to hand on from the Members page (PD-0042):
 * a QR icon in the header opens them in a dialog, as the app's sheet does. The settings stay under Website.
 * The booking-page settings load with the page, so the dialog never opens empty. Also on the Home header (PD-0057).
 */
interface ShareLinksButtonProps {
  testId?: string;
}

const ShareLinksButton = ({ testId = "members-share-links" }: ShareLinksButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchBookingPageSettings());
  }, [dispatch]);

  const portalUrl = `${window.location.origin}${ROUTES.PORTAL_LOGIN}`;
  const pageUrl = settings?.enabled && settings.slug ? bookingPageUrl(settings.slug) : null;
  const close = (): void => setOpen(false);

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary px-3"
        aria-label={t("shareLinks.title")}
        title={t("shareLinks.title")}
        data-testid={testId}
        onClick={() => setOpen(true)}
      >
        <QrCode size={18} />
      </button>
      {open && (
        <ModalShell onClose={close} labelledBy={TITLE_ID}>
          <div className="p-4 lg:p-6">
            <h2 id={TITLE_ID} className="text-lg font-semibold text-grey-20 dark:text-white">
              {t("shareLinks.title")}
            </h2>
            <Link to={ROUTES.WEBSITE} className="text-sm text-primary-40 dark:text-primary-90 hover:underline">
              {t("shareLinks.settings")}
            </Link>
            <div className="mt-2 divide-y divide-grey-90 dark:divide-grey-30">
              <ShareLinkBlock id="portal" label={t("shareLinks.portal")} hint={t("shareLinks.portalHint")} url={portalUrl} />
              {pageUrl ? (
                <ShareLinkBlock id="booking-page" label={t("shareLinks.bookingPage")} url={pageUrl} />
              ) : (
                settings && (
                  <p data-testid="share-no-booking-page" className="py-5 text-center text-sm text-grey-40 dark:text-grey-80">
                    {t("shareLinks.noBookingPage")}
                  </p>
                )
              )}
            </div>
            <div className="mt-2 flex justify-end">
              <button type="button" className="btn btn-secondary" onClick={close}>
                {t("common.done")}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </>
  );
};

export default ShareLinksButton;
